import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Droplets, Shield, AlertCircle, Check } from 'lucide-react';
import dataroomService from '../services/dataroomService';
import toast from 'react-hot-toast';

const WatermarkSettings = ({ isOpen, onClose, dataroomUuid, currentStatus = false, onUpdate }) => {
  const [watermarkEnabled, setWatermarkEnabled] = useState(currentStatus);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setWatermarkEnabled(currentStatus);
  }, [currentStatus]);

  const handleToggle = async () => {
    try {
      setLoading(true);
      const newStatus = !watermarkEnabled;
      
      const response = await dataroomService.toggleWatermark(dataroomUuid, newStatus);
      
      if (response.success || response.status === 'success' || response.data) {
        setWatermarkEnabled(newStatus);
        toast.success(`Watermark ${newStatus ? 'enabled' : 'disabled'} successfully`);
        // Update parent component state
        if (onUpdate) {
          onUpdate(newStatus);
        }
      } else {
        toast.error('Failed to update watermark settings');
      }
    } catch (error) {
      toast.error('Failed to update watermark settings');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
      >
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Droplets className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Watermark Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-900">About Watermarks</h3>
                <p className="text-sm text-blue-700 mt-1">
                  When enabled, all documents viewed or downloaded from this dataroom will have a watermark applied with the viewer's information and timestamp.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Enable Watermark</h3>
              <p className="text-sm text-gray-500 mt-1">
                Apply watermarks to all documents in this dataroom
              </p>
            </div>
            <button
              onClick={handleToggle}
              disabled={loading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                watermarkEnabled ? 'bg-primary-600' : 'bg-gray-200'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  watermarkEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {watermarkEnabled && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Watermark Information</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  Viewer's name and email
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  Access timestamp
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-3 text-sm text-gray-500">
            <Shield className="w-4 h-4" />
            <p>Watermarks help protect sensitive documents by tracking access and deterring unauthorized sharing.</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default WatermarkSettings;